<template>
  <div v-if="positionsInitialLoading" class="table-group">
    <div class="table-loader"></div>
  </div>

  <div v-if="positionsError" class="mc-error">{{ positionsError }}</div>

  <template v-if="positionsLoaded">
    <div class="header-wrapper header-wrapper--row">
      <div class="header-group flex-column">
        <div class="flex">
          <div class="h1">{{ t('pageTitleOrdersAll') }}</div>
        </div>
      </div>
      <div class="buttons-row">
        <a class="btn" @click="showDates = true"> {{ t('orderPage.importExcel') }}</a>
        <a class="btn" :class="{loader:processExport}" @click="exportExcel"> {{ t('orderPage.exportExcel') }}</a>
      </div>
    </div>

    <Positions
        ref="positionsTable"
        mode="all"
        :order="{}"
        :positions="positions"
        :pagination="positionsPagination"
        :server-sort="positionsSort"
        :filter-options="filterOptions"
        :initial-filters="currentFilters()"
        :loading="positionsLoading"
        :orderLoaded="positionsLoaded"
        @change-page="changePage"
        @change-sort="changeSort"
        @change-filters="changeFilters"
    ></Positions>

    <Dates :isOpen="showDates" @close="showDates = false" @imported="handlePositionsImported" />
  </template>
</template>

<script>
import {mapActions, mapGetters} from "vuex";

import Dates from '@/components/order/Dates.vue';
import Positions from '@/components/order/Positions.vue';
import importexport from '@/mixin/importexport.js';

import { useI18n } from 'vue-i18n';

export default {
  components: {
    Positions,
    Dates
  },
  mixins: [importexport],
  data: () => ({
    showAttachment: false,
    showHistory: false,
    showEdit: false,
    showSettings: false,
    showDates: false,

    sortKey: '',
    sortDir: 'asc',

    filtersState: {
      selectedStorage_delivery: [],
      selectedDates: {},
      selectedEmpty_date: [],
      showArrived: false,
      invoiceOrderSearch: '',
      selectedOrder: [],
      selectedFilial: [],
      selectedPartner: [],
      selectedColor: [],
    },
    datesOptions: [
      {
        label: 'Дата отгрузки',
        value: 'date_order_shipment'
      },
      {
        label: 'Дата размещения заказа поставщиком',
        value: 'date_supplier_order'
      },
      {
        label: 'Дата прибытия товара на склад поставщика',
        value: 'date_arrival_supplier'
      },
      {
        label: 'Дата готовности товара к отгрузке на складе поставщика',
        value: 'date_ready_shipment'
      },
      {
        label: 'Планируемая дата поступления на склад поставщика',
        value: 'date_planned_arrival'
      }
    ],
    empty_dateOptions: [
      {
        label: 'Дата отгрузки',
        value: 'date_order_shipment'
      },
      {
        label: 'Дата размещения заказа поставщиком',
        value: 'date_supplier_order'
      },
      {
        label: 'Дата прибытия товара на склад поставщика',
        value: 'date_arrival_supplier'
      },
    ]

  }),
  setup() {
    const { t } = useI18n();
    return { t };
  },
  created() {
    this.load();
  },
  watch: {
    '$route.query'() {
      this.load();
    },
  },
  computed: {
    ...mapGetters('positions', [
      'positions',
      'positionsPagination',
      'positionsSort',
      'filterOptions',
      'positionsLoading',
      'positionsError',
      'positionsLoaded',
    ]),
    positionsInitialLoading() {
      return this.positionsLoading && !this.positionsLoaded;
    },
  },
  methods: {
    ...mapActions('positions', [
      'fetchPositions',
    ]),
    async load() {
      await this.fetchPositions({
        page: this.currentPage(),
        sort: this.currentSort(),
        sortDir: this.currentSortDir(),
        filters: this.currentFilterParams(),
      });
    },
    currentPage() {
      return parseInt(this.$route.query.page, 10) || 1;
    },
    changePage(page) {
      if (page === this.currentPage()) {
        return;
      }

      this.$router.push({
        name: this.$route.name,
        query: {
          ...this.$route.query,
          page,
        },
      });
    },
    currentSort() {
      return this.$route.query.sort || '';
    },
    currentSortDir() {
      return this.$route.query.sort_dir === 'desc' ? 'desc' : 'asc';
    },
    changeSort(sort) {
      this.$router.push({
        name: this.$route.name,
        query: {
          ...this.$route.query,
          page: 1,
          sort: sort.key || undefined,
          sort_dir: sort.dir || undefined,
        },
      });
    },
    changeFilters(filters) {
      this.$router.push({
        name: this.$route.name,
        query: {
          ...this.queryWithoutFilters(),
          page: 1,
          ...this.buildFilterQuery(filters),
        },
      });
    },
    currentFilters() {
      const query = this.$route.query;
      const dates = {};

      Object.keys(query).forEach(key => {
        if (key.startsWith('f_date_') && query[key]) {
          dates[key.replace('f_date_', '')] = String(query[key]);
        }
      });

      return {
        order: this.queryArray(query.f_order),
        partner: this.queryArray(query.f_partner),
        filial: this.queryArray(query.f_filial),
        color: this.queryArray(query.f_color),
        storage_delivery: this.queryArray(query.f_storage_delivery),
        empty_date: this.queryArray(query.f_empty_date),
        dates,
        arrived: query.f_arrived === '1',
        search: query.f_search || '',
      };
    },
    currentFilterParams() {
      return this.buildFilterQuery(this.currentFilters());
    },
    queryArray(value) {
      if (Array.isArray(value)) {
        return value.filter(item => item !== '');
      }
      return value ? [value] : [];
    },
    queryWithoutFilters() {
      return Object.entries(this.$route.query).reduce((result, [key, value]) => {
        if (key !== 'page' && !key.startsWith('f_')) {
          result[key] = value;
        }
        return result;
      }, {});
    },
    buildFilterQuery(filters) {
      const query = {
        f_order: filters.order?.length ? filters.order : undefined,
        f_partner: filters.partner?.length ? filters.partner : undefined,
        f_filial: filters.filial?.length ? filters.filial : undefined,
        f_color: filters.color?.length ? filters.color : undefined,
        f_storage_delivery: filters.storage_delivery?.length ? filters.storage_delivery : undefined,
        f_empty_date: filters.empty_date?.length ? filters.empty_date : undefined,
        f_arrived: filters.arrived ? '1' : undefined,
        f_search: filters.search || undefined,
      };

      Object.entries(filters.dates || {}).forEach(([key, value]) => {
        if (value) {
          query[`f_date_${key}`] = value;
        }
      });

      return query;
    },
  }
}
</script>
<style>
.allpositions .table td {padding: 6px 10px;}
</style>
